import React, { useState, useEffect } from 'react';
import { Phone, Check } from 'lucide-react';
import apiService from './services/api';
import { Panel, LoadingSpinner } from './components';

function PhoneForwardingPage() {
  const [options, setOptions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);
  const [success, setSuccess] = useState('');

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiService.baseURL}/amn/phone_config`);
      if (response.ok) {
        const data = await response.json();
        setOptions(data.options || []);
        setActiveId(data.active_id);
      }
    } catch {}
    setLoading(false);
  };

  const handleSwitch = async (id) => {
    setSwitching(id);
    setSuccess('');
    try {
      const response = await fetch(`${apiService.baseURL}/amn/phone_config/numbers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'set_active', id }),
      });
      if (response.ok) {
        const data = await response.json();
        setActiveId(id);
        setSuccess(`Calls now forwarding to ${data.active_option?.label}`);
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch {}
    setSwitching(null);
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length >= 10) {
      const last10 = digits.slice(-10);
      return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
    }
    return phone;
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <Phone className="w-5 h-5 text-gray-600" />
          <h1 className="text-lg font-semibold text-gray-900">Phone Forwarding</h1>
        </div>
        <p className="text-xs text-gray-500 mt-0.5">Select who receives incoming calls</p>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex justify-center py-12"><LoadingSpinner /></div>
        ) : (
          <>
            {success && (
              <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600" />
                <p className="text-sm text-green-800">{success}</p>
              </div>
            )}

            <div className="max-w-lg mx-auto space-y-3">
              {options.map(opt => {
                const isActive = opt.id === activeId;
                return (
                  <Panel
                    key={opt.id}
                    className={`p-4 cursor-pointer transition-all ${
                      isActive
                        ? 'ring-2 ring-green-500 bg-green-50'
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => !isActive && handleSwitch(opt.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isActive ? 'bg-green-100' : 'bg-gray-100'
                        }`}>
                          <Phone className={`w-5 h-5 ${isActive ? 'text-green-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
                          <p className="text-xs text-gray-500">{formatPhone(opt.number)}</p>
                        </div>
                      </div>
                      {isActive ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          <Check className="w-3 h-3" />
                          Active
                        </span>
                      ) : switching === opt.id ? (
                        <span className="text-xs text-gray-400">Switching...</span>
                      ) : (
                        <span className="text-xs text-gray-400">Tap to switch</span>
                      )}
                    </div>
                  </Panel>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PhoneForwardingPage;
